import { useCallback, useEffect, useState } from 'react'
import {
  ApiError,
  api,
  can,
  refusalReason,
  shortDate,
  tierLabel,
  type PublishResult,
  type QuestionnaireDetail,
  type VersionDetail,
  type VersionDiff,
} from '../lib/api'
import { ErrorBox, Loading } from '../components/DataTable'
import { QuestionnaireEditor } from '../components/QuestionnaireEditor'
import { Status, Toggletip, type Tone } from '../components/kit'
import { ReasonTip } from '../components/vendor/ReasonTip'
import '../styles/studio.css'
import { Crumbs } from '../components/Crumbs'
import '../components/kit/back.css'
import '../styles/sweep.css'
import { DiffTable, diffSummary } from '../components/QuestionTable'

// A version's state as a label. Retired is finished, so it goes quiet; a code
// the console does not know still shows, in words rather than capitals.
const VERSION_STATUS: Record<string, { tone: Tone; label: string }> = {
  DRAFT: { tone: 'info', label: 'Draft' },
  PUBLISHED: { tone: 'ok', label: 'Published' },
  RETIRED: { tone: 'plain', label: 'Retired' },
}

function VersionStatus({ status }: { status: string }) {
  const known = VERSION_STATUS[status]
  if (known) return <Status tone={known.tone}>{known.label}</Status>
  const words = status.toLowerCase().replace(/_/g, ' ')
  return <Status tone="neutral">{words.charAt(0).toUpperCase() + words.slice(1)}</Status>
}

function shortHash(h: string | undefined) {
  return h ? h.slice(0, 12) : ''
}

export function QuestionnaireStudio({ id }: { id: string }) {
  const [detail, setDetail] = useState<QuestionnaireDetail | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [openVersionId, setOpenVersionId] = useState<string | null>(null)
  const [version, setVersion] = useState<VersionDetail | null>(null)
  const [versionError, setVersionError] = useState<unknown>(null)
  const [publishing, setPublishing] = useState(false)
  const [publishError, setPublishError] = useState<ApiError | null>(null)
  const [published, setPublished] = useState<PublishResult | null>(null)
  const [drafting, setDrafting] = useState(false)
  const [draftError, setDraftError] = useState<ApiError | null>(null)
  const [diffFrom, setDiffFrom] = useState('')
  const [diffTo, setDiffTo] = useState('')
  const [diff, setDiff] = useState<VersionDiff | null>(null)
  const [diffError, setDiffError] = useState<unknown>(null)
  // Archive and restore (review 3.2). Archiving asks first, with an optional
  // reason that goes on the audit trail; restoring is one press, because it
  // only puts the questionnaire back where it was.
  const [confirmArchive, setConfirmArchive] = useState(false)
  const [archiveReason, setArchiveReason] = useState('')
  const [archiving, setArchiving] = useState(false)
  const [archiveError, setArchiveError] = useState<ApiError | null>(null)
  const [archiveSaid, setArchiveSaid] = useState('')
  const load = useCallback(() => {
    api
      .questionnaire(id)
      .then((d) => {
        setDetail(d)
        setError(null)
        // Open whatever is being worked on. A draft is what somebody came here
        // for; a published version is what they came to read.
        setOpenVersionId((prev) => prev ?? d.questionnaire.draftVersionId ?? d.questionnaire.publishedVersionId ?? null)
      })
      .catch(setError)
  }, [id])

  useEffect(load, [load])

  useEffect(() => {
    if (!openVersionId) {
      setVersion(null)
      return
    }
    let live = true
    setVersion(null)
    setVersionError(null)
    api
      .questionnaireVersion(openVersionId)
      .then((v) => {
        if (live) setVersion(v)
      })
      .catch((err) => {
        if (live) setVersionError(err)
      })
    return () => {
      live = false
    }
  }, [openVersionId])

  function newDraft(cloneFrom?: string) {
    setDrafting(true)
    setDraftError(null)
    setPublished(null)
    api
      .createQuestionnaireVersion(id, { cloneFromVersionId: cloneFrom })
      .then((r) => {
        setOpenVersionId(r.id)
        load()
      })
      .catch((err) => setDraftError(err instanceof ApiError ? err : new ApiError(0, 'UNKNOWN', String(err))))
      .finally(() => setDrafting(false))
  }

  function publish() {
    if (!openVersionId) return
    setPublishing(true)
    setPublishError(null)
    api
      .publishQuestionnaireVersion(openVersionId)
      .then((r) => {
        setPublished(r)
        load()
        return api.questionnaireVersion(openVersionId).then(setVersion)
      })
      .catch((err) => setPublishError(err instanceof ApiError ? err : new ApiError(0, 'UNKNOWN', String(err))))
      .finally(() => setPublishing(false))
  }

  function setArchived(archive: boolean) {
    setArchiving(true)
    setArchiveError(null)
    setArchiveSaid('')
    const call = archive ? api.archiveQuestionnaire(id, archiveReason.trim()) : api.restoreQuestionnaire(id)
    call
      .then((r) => {
        setConfirmArchive(false)
        setArchiveReason('')
        const open = r.openAssessments
        setArchiveSaid(
          archive
            ? 'Archived, no longer offered when a questionnaire is picked' +
                (open > 0 ? '. ' + open + ' open assessment' + (open === 1 ? '' : 's') + ' sent with it continue' : '')
            : 'Restored, offered again wherever a questionnaire is picked',
        )
        load()
      })
      .catch((err) => setArchiveError(err instanceof ApiError ? err : new ApiError(0, 'UNKNOWN', String(err))))
      .finally(() => setArchiving(false))
  }

  function runDiff() {
    if (!diffFrom || !diffTo) return
    setDiff(null)
    setDiffError(null)
    api.questionnaireDiff(diffFrom, diffTo).then(setDiff).catch(setDiffError)
  }

  if (error) return <ErrorBox error={error} what="This questionnaire" />

  if (!detail) return <Loading what="the questionnaire" />

  const q = detail.questionnaire

  const versions = detail.versions
  const mayAuthor = can('questionnaire.author')
  const authorWhy = mayAuthor ? undefined : refusalReason('questionnaire.author')

  return (
    <>
      <div className="page-head">
        <div>
          <Crumbs className="kit-crumbs" home={{ label: 'Questionnaires', href: '#/questionnaires' }} current={q.name} />
          <h1>
            <span className="mono">{q.code}</span> {q.name}
          </h1>
          {/* What the questionnaire is for, as a label, and the one thing that
              follows from it. */}
          <p className="muted sw-row">
            {q.isArchived && <Status tone="plain">Archived</Status>}
            {q.purpose === 'ASSESSMENT' ? (
              <>
                <Status tone="info">Scored</Status>
                <span>Publishing checks every question a bound risk model weighs keeps its key</span>
              </>
            ) : q.purpose === 'PROFILING' ? (
              <>
                <Status tone="neutral">Profiling</Status>
                <span>Feeds the inherent tier through the vendor profile, no weights read it</span>
              </>
            ) : (
              <>
                <Status tone="plain">Never scored</Status>
                <span>Collected and stored</span>
              </>
            )}
          </p>
        </div>
        <div className="studio-header-actions">
          {q.isArchived ? (
            <button
              type="button"
              className="primary"
              disabled={archiving || !mayAuthor}
              title={authorWhy}
              onClick={() => setArchived(false)}
              data-action="tprm.questionnaire.a05"
            >
              {archiving ? 'Restoring' : 'Restore'}
            </button>
          ) : (
            <>
              <button
                type="button"
                className="linkish"
                disabled={archiving || !mayAuthor}
                title={authorWhy}
                aria-expanded={confirmArchive}
                onClick={() => {
                  setArchiveError(null)
                  setConfirmArchive((v) => !v)
                }}
              >
                Archive
              </button>
              {q.draftVersionId ? (
                <button type="button" onClick={() => setOpenVersionId(q.draftVersionId!)}>
                  Open draft v{q.draftVersionNo}
                </button>
              ) : (
                <button
                  type="button"
                  className="primary"
                  disabled={drafting || !mayAuthor}
                  title={authorWhy}
                  onClick={() => newDraft(q.publishedVersionId)}
                  data-action="tprm.questionnaire.a02"
                >
                  {drafting ? 'Creating' : q.publishedVersionId ? 'Draft the next version' : 'Start the first version'}
                </button>
              )}
            </>
          )}
        </div>
      </div>
      {/* Just archived, the confirmation says this and more; the standing
          note is for anybody who opens it later. */}
      {q.isArchived && !archiveSaid && (
        <p className="note sw-row" role="note">
          <span>Not offered when a questionnaire is picked. Assessments already sent with it continue</span>
          <Toggletip label="What archiving keeps">
            Every version and every answer given against it stay as they were. Restore it to send it again or to draft a new version.
          </Toggletip>
        </p>
      )}
      {archiveSaid && (
        <div className="ok-note" role="status">
          <p className="sw-row" style={{ margin: 0 }}>
            <span>{archiveSaid}</span>
            <button type="button" className="linkish" onClick={() => setArchiveSaid('')}>
              Dismiss
            </button>
          </p>
        </div>
      )}
      {archiveError && <ErrorBox error={archiveError} what="The questionnaire" verb={q.isArchived ? 'restored' : 'archived'} />}
      {confirmArchive && !q.isArchived && (
        <form
          className="confirm"
          style={{ marginBottom: 'var(--gap)' }}
          onSubmit={(e) => {
            e.preventDefault()
            setArchived(true)
          }}
        >
          <strong>Archive {q.code}?</strong>
          <p>It stops being offered when a questionnaire is picked. Nothing already sent changes, and it can be restored</p>
          <div className="field" style={{ maxWidth: 520 }}>
            <label htmlFor="archive-reason">Reason</label>
            <input
              id="archive-reason"
              value={archiveReason}
              maxLength={500}
              placeholder="Replaced by the 2027 set"
              onChange={(e) => setArchiveReason(e.target.value)}
            />
          </div>
          <div className="chiprow">
            <button type="submit" className="primary" disabled={archiving} data-action="tprm.questionnaire.a04">
              {archiving ? 'Archiving' : 'Yes, archive'}
            </button>
            <button type="button" onClick={() => setConfirmArchive(false)} disabled={archiving}>
              Not now
            </button>
          </div>
        </form>
      )}
      {draftError && <ErrorBox error={draftError} what="The draft" />}
      {detail.boundRiskModels.length > 0 && (
        <div className="panel panel-flush">
          <h2>Risk models that score this</h2>
          <ul className="rule-list">
            {detail.boundRiskModels.map((m) => (
              <li key={m.riskModelVersionId}>
                <span className="mono">{m.code}</span> {m.name} · version {m.versionNo}
                {m.tiers.length > 0 && (
                  <span className="muted"> · bound through {m.tiers.map((t) => tierLabel(t)).join(', ')}</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
      <div className="panel">
        <h2>Versions</h2>
        {versions.length === 0 ? (
          <div className="empty">
            <strong>No versions yet</strong>
            It asks nothing yet, start the first version to add sections and questions
          </div>
        ) : (
          <div className="table-wrap">
            {/* A floor on the width, so a phone scrolls the table inside its
                own frame rather than breaking every heading letter by letter. */}
            <table style={{ minWidth: 820 }}>
              <caption className="vh">Versions of {q.code}</caption>
              <thead>
                <tr>
                  <th scope="col">Version</th>
                  <th scope="col">Status</th>
                  <th scope="col" className="num">
                    Questions
                  </th>
                  <th scope="col">Content hash</th>
                  <th scope="col">Authored by</th>
                  <th scope="col">Published by</th>
                  <th scope="col">
                    <span className="vh">Open</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {versions.map((v) => (
                  <tr key={v.id} aria-current={v.id === openVersionId ? 'true' : undefined}>
                    <th scope="row">v{v.versionNo}</th>
                    <td>
                      <VersionStatus status={v.status} />
                    </td>
                    <td className="num">{v.questionCount}</td>
                    <td>
                      {v.contentSha256 ? (
                        <span className="mono" title={v.contentSha256}>
                          {shortHash(v.contentSha256)}
                        </span>
                      ) : (
                        <span className="muted">not stamped until published</span>
                      )}
                    </td>
                    <td>{v.createdBy}</td>
                    <td>
                      {v.publishedBy ? (
                        <>
                          {v.publishedBy}
                          <span className="cell-sub">{v.publishedAt ? shortDate(v.publishedAt) : ''}</span>
                        </>
                      ) : (
                        <span className="muted">not published</span>
                      )}
                    </td>
                    <td>
                      <button type="button" onClick={() => setOpenVersionId(v.id)}>
                        {v.id === openVersionId ? 'Open' : 'View'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {versions.length > 1 && (
        <div className="panel">
          <h2>Compare two versions</h2>
          <div className="pickrow">
            <div className="field">
              <label htmlFor="diff-from">From</label>
              <select id="diff-from" value={diffFrom} onChange={(e) => setDiffFrom(e.target.value)}>
                <option value="">Choose a version</option>
                {versions.map((v) => (
                  <option key={v.id} value={v.id}>
                    v{v.versionNo} ({v.status})
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label htmlFor="diff-to">To</label>
              <select id="diff-to" value={diffTo} onChange={(e) => setDiffTo(e.target.value)}>
                <option value="">Choose a version</option>
                {versions.map((v) => (
                  <option key={v.id} value={v.id}>
                    v{v.versionNo} ({v.status})
                  </option>
                ))}
              </select>
            </div>
            <button type="button" onClick={runDiff} disabled={!diffFrom || !diffTo || diffFrom === diffTo}>
              Compare
            </button>
          </div>
          {diffError != null && <ErrorBox error={diffError} what="The comparison" />}
          {diff && (
            <>
              <p className="summary-line">{diffSummary(diff)}</p>
              <DiffTable diff={diff} />
              {/* Numbers first; what happens to those assessments is the API's
                  own sentence, kept whole behind the (i). */}
              <p className="note sw-row">
                <span>
                  {diff.inFlight.openAssessmentsOnFromVersion} open assessment
                  {diff.inFlight.openAssessmentsOnFromVersion === 1 ? '' : 's'} on v{diff.from.versionNo}
                </span>
                <ReasonTip label="What happens to open assessments" text={diff.inFlight.note} />
              </p>
            </>
          )}
        </div>
      )}
      {publishError && <ErrorBox error={publishError} what="The publish" />}
      {/* The confirmation is the count and the hash; the API's note on what
          publishing froze is kept word for word behind the (i). */}
      {published && (
        <div className="ok-note" role="status">
          <p className="sw-row">
            <strong>
              Version {published.versionNo} published, {published.questionCount} question
              {published.questionCount === 1 ? '' : 's'}, hash {shortHash(published.contentSha256)}
            </strong>
            <ReasonTip label="What publishing froze" text={published.note} />
          </p>
          {published.riskModelsChecked.length > 0 && (
            <p className="muted">Checked against {published.riskModelsChecked.join(', ')}</p>
          )}
        </div>
      )}
      {versionError != null && <ErrorBox error={versionError} what="That version" />}
      {openVersionId != null && !version && versionError == null && <Loading what="the version" />}
      {version && (
        <>
          <div className="section-head">
            <div>
              <h2 className="sw-title">
                Version {version.version.versionNo} <VersionStatus status={version.version.status} />
              </h2>
              {version.openAssessments > 0 && (
                <p className="muted">
                  {version.openAssessments} open assessment{version.openAssessments === 1 ? '' : 's'} on this
                  version{' '}
                  <Toggletip label="What happens to open assessments">
                    Nothing migrates when a newer version is published: they stay here and keep answering the questions
                    they were sent.
                  </Toggletip>
                </p>
              )}
            </div>
            {version.editable && !q.isArchived && (
              <button
                type="button"
                className="primary"
                onClick={publish}
                disabled={publishing || !can('questionnaire.publish')}
                title={can('questionnaire.publish') ? undefined : refusalReason('questionnaire.publish')}
                data-action="tprm.questionnaire.a03"
              >
                {publishing ? 'Publishing' : 'Publish this version'}
              </button>
            )}
          </div>
          <QuestionnaireEditor
            key={version.version.id}
            versionId={version.version.id}
            sections={version.sections}
            editable={version.editable && !q.isArchived}
            editNote={q.isArchived ? 'This questionnaire is archived. Restore it to change it.' : version.editNote}
            onSaved={(saved) => {
              // A draft that already carried questions is retired on save and
              // the content lands on a new version. Staying pointed at the
              // retired one means the next save supersedes again, and the
              // author is editing a row nobody will ever publish.
              if (saved.id !== version.version.id) setOpenVersionId(saved.id)
              load()
            }}
          />
          {!version.editable && !q.isArchived && (
            <div className="commit-bar">
              <button
                type="button"
                onClick={() => newDraft(version.version.id)}
                disabled={drafting || !can('questionnaire.author')}
                title={can('questionnaire.author') ? undefined : refusalReason('questionnaire.author')}
              >
                {drafting ? 'Creating' : 'Draft the next version from this one'}
              </button>
            </div>
          )}
        </>
      )}
    </>
  )
}
